import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { ConfirmationModal } from '../Partials/ConfirmationModal';

interface ConfirmOptions {
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Optional third action, rendered as a link. Use it to send the user somewhere
   * to look rather than to answer the question — it dismisses without confirming.
   */
  alternateLabel?: string;
  onConfirm: () => unknown | Promise<unknown>;
  onCancel?: () => unknown | Promise<unknown>;
  onAlternate?: () => unknown | Promise<unknown>;
}

type ConfirmFunction = (options: ConfirmOptions) => Promise<void>;

const ConfirmationModalContext = createContext<ConfirmFunction | undefined>(undefined);

/**
 * Hook to access the confirmation modal function.
 *
 * Shows a styled confirmation modal and calls the appropriate callback.
 * Returns a promise that resolves after the modal is dismissed and the callback completes.
 *
 * @example
 * ```tsx
 * const confirm = useConfirmationModal();
 *
 * const handleDelete = () =>
 *   confirm({
 *     title: 'Delete Item',
 *     message: 'Are you sure?',
 *     onConfirm: () => deleteItem()
 *   });
 *
 * // Wait for modal to close and callback to complete
 * const handleDelete = async () => {
 *   await confirm({
 *     title: 'Delete Item',
 *     message: 'Are you sure?',
 *     onConfirm: async () => await deleteItem()
 *   });
 *   console.log('Modal closed and deletion complete');
 * };
 * ```
 */
export const useConfirmationModal = (): ConfirmFunction => {
  const context = useContext(ConfirmationModalContext);
  if (!context) {
    throw new Error('useConfirmationModal must be used within a ConfirmationModalProvider');
  }
  return context;
};

interface ModalState extends ConfirmOptions {
  visible: boolean;
  resolve: () => void;
}

export const ConfirmationModalProvider = ({ children }: { children: ReactNode }) => {
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const confirm: ConfirmFunction = useCallback(
    options =>
      new Promise<void>(resolve => {
        setModalState({ ...options, visible: true, resolve });
      }),
    [],
  );

  const handleConfirm = useCallback(async () => {
    if (!modalState) return;

    setModalState(prev => prev ? { ...prev, visible: false } : null);

    await modalState.onConfirm();
    modalState.resolve();
  }, [modalState]);

  const handleCancel = useCallback(async () => {
    if (!modalState) return;

    setModalState(prev => prev ? { ...prev, visible: false } : null);

    await modalState.onCancel?.();
    modalState.resolve();
  }, [modalState]);

  const handleAlternate = useCallback(async () => {
    if (!modalState) return;

    setModalState(prev => prev ? { ...prev, visible: false } : null);

    await modalState.onAlternate?.();
    modalState.resolve();
  }, [modalState]);

  return (
    <ConfirmationModalContext.Provider value={confirm}>
      {children}
      <ConfirmationModal
        visible={modalState?.visible ?? false}
        title={modalState?.title}
        message={modalState?.message}
        confirmLabel={modalState?.confirmLabel}
        cancelLabel={modalState?.cancelLabel}
        alternateLabel={modalState?.alternateLabel}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onAlternate={modalState?.onAlternate ? handleAlternate : undefined}
      />
    </ConfirmationModalContext.Provider>
  );
};
